<template>
  <teleport to="body">
    <transition name="drawer" appear :duration="300">
      <div v-if="model" class="fixed inset-0 z-50 flex items-end justify-center">
        <div class="drawer-overlay absolute inset-0 bg-n-900/60" @click="close()" />
        <div
          class="relative w-full max-w-[30rem] bg-[#2F2D45] rounded-t-2xl overflow-hidden pb-safe drawer-content"
          :style="transformStyle"
        >
          <div
            class="flex justify-center pt-4 pb-6 cursor-grab active:cursor-grabbing"
            @touchstart="handleDragStart"
            @touchmove="handleDragMove"
            @touchend="handleDragEnd"
            @mousedown="handleDragStart"
          >
            <div class="w-24 h-2 bg-[#5D5C8C] rounded-full" />
          </div>

          <div class="max-h-[80vh] min-h-[33vh] overflow-y-auto flex">
            <slot />
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue'

const emit = defineEmits<{
  close: []
}>()

const model = defineModel<boolean>({
  default: false
})

const isDragging = ref(false)
const startY = ref(0)
const dragDistance = ref(0)

let animationFrameId: number | null = null

const transformStyle = computed(() => {
  if (dragDistance.value === 0) {
    return {}
  }
  return {
    transform: `translateY(${dragDistance.value}px)`,
    transition: isDragging.value ? 'none' : 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)'
  }
})

function close() {
  model.value = false
  emit('close')
}

function updateDragDistance(clientY: number) {
  const newDistance = Math.max(0, clientY - startY.value)

  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
  }

  animationFrameId = requestAnimationFrame(() => {
    dragDistance.value = newDistance
    animationFrameId = null
  })
}

function handleDragStart(event: TouchEvent | MouseEvent) {
  isDragging.value = true
  dragDistance.value = 0

  if ('touches' in event) {
    startY.value = event.touches[0].clientY
  } else {
    startY.value = event.clientY
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
  }
}

function handleDragMove(event: TouchEvent) {
  if (!isDragging.value || !('touches' in event)) return

  event.preventDefault()
  updateDragDistance(event.touches[0].clientY)
}

function handleMouseMove(event: MouseEvent) {
  if (!isDragging.value) return

  event.preventDefault()
  updateDragDistance(event.clientY)
}

function handleDragEnd() {
  if (!isDragging.value) return

  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
    animationFrameId = null
  }

  const shouldClose = dragDistance.value > 80

  isDragging.value = false

  if (shouldClose) {
    dragDistance.value = 0
    close()
  } else {
    dragDistance.value = 0
  }
}

function handleMouseUp() {
  if (isDragging.value) {
    window.removeEventListener('mousemove', handleMouseMove)
    window.removeEventListener('mouseup', handleMouseUp)
    handleDragEnd()
  }
}

onUnmounted(() => {
  window.removeEventListener('mousemove', handleMouseMove)
  window.removeEventListener('mouseup', handleMouseUp)
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
  }
})
</script>

<style scoped>
.drawer-enter-active,
.drawer-leave-active {
  transition: none;
}

/* Overlay fades */
.drawer-overlay {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.drawer-enter-from .drawer-overlay,
.drawer-leave-to .drawer-overlay {
  opacity: 0;
}

/* Content slides */
.drawer-content {
  transform: translateY(0);
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
}

.drawer-enter-from .drawer-content,
.drawer-leave-to .drawer-content {
  /* Force transform on Safari */
  transform: translateY(100%) !important;
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1) !important;
}
</style>
